import React, { useCallback, useMemo, useState } from "react";
import { NotFoundError, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { ToastNotificationNoContent } from "../../elements/toast-notification-builtins";
import { useVelavuToast } from "../../elements/velavu-toast";
import { DeviceDetailTab } from "./dropdown/device-detail/device-detail";
import { useSubscribeCombinedDeviceUpdates } from "../../emitter/stream-helper";

interface InternalDashboardSelectionState {
	device: VelavuDevice | undefined;
	deviceDetailTab: DeviceDetailTab;
}

export interface DashboardSelectionState {
	device: VelavuDevice | undefined;
	select: (selection: VelavuDevice | undefined) => void;
	deviceDetailTab: DeviceDetailTab;
	setDeviceDetailTab: (tab: DeviceDetailTab) => void;
	refresh: () => Promise<void>;
}

/**
 * State for the device or asset currently selected on the dashboard.
 */
export function useDashboardSelectionState(): DashboardSelectionState {
	const { pushToast } = useVelavuToast();
	const [selection, setSelection] = useState<InternalDashboardSelectionState>(
		{
			device: undefined,
			deviceDetailTab: DeviceDetailTab.Info,
		},
	);

	const select = useCallback((nextDevice: VelavuDevice | undefined) => {
		setSelection((previousState) => {
			//Revert to the info tab if we're selecting a new device
			let tab: DeviceDetailTab;
			if (previousState.device?.id === nextDevice?.id) {
				tab = previousState.deviceDetailTab;
			} else {
				tab = DeviceDetailTab.Info;
			}

			return {
				device: nextDevice,
				deviceDetailTab: tab,
			};
		});
	}, []);

	const clearMissingSelection = useCallback(() => {
		setSelection({
			device: undefined,
			deviceDetailTab: DeviceDetailTab.Info,
		});
		pushToast((close) => <ToastNotificationNoContent onDismiss={close} />);
	}, [pushToast]);

	const refresh = useCallback(async () => {
		//Nothing to do if we have no selection
		if (selection.device === undefined) {
			return;
		}

		//Fetch the updated object
		let updatedDevice: VelavuDevice;
		try {
			updatedDevice = await VelavuAPI.devices.getSpecificDevice(
				selection.device.id,
			);
		} catch (err) {
			if (err instanceof NotFoundError) {
				clearMissingSelection();
				return;
			}

			throw err;
		}

		setSelection((prevSelection) => ({
			device: updatedDevice,
			deviceDetailTab: prevSelection.deviceDetailTab,
		}));
	}, [selection, clearMissingSelection]);

	useSubscribeCombinedDeviceUpdates(
		useCallback(
			(update) => {
				if (update.id === selection.device?.id) {
					const device = update.data;
					if (device === null) {
						clearMissingSelection();
					} else {
						setSelection((prevSelection) => ({
							device: device,
							deviceDetailTab: prevSelection.deviceDetailTab,
						}));
					}
				}
			},
			[selection, clearMissingSelection, setSelection],
		),
	);

	const deviceDetailTab = selection.deviceDetailTab;
	const setDeviceDetailTab = useCallback(
		(tab: DeviceDetailTab) => {
			setSelection((prevSelection) => ({
				device: prevSelection.device,
				deviceDetailTab: tab,
			}));
		},
		[setSelection],
	);

	return useMemo(
		(): DashboardSelectionState => ({
			device: selection.device,
			select,
			deviceDetailTab,
			setDeviceDetailTab,
			refresh,
		}),
		[selection, select, deviceDetailTab, refresh],
	);
}
